import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetArchiveLoanFolder } from '../../../services/loanService';
import { ArchiveFolderDocuments, ArchiveHeader, AttentionList, LOAN_FOLDERS, LoanStatusPill, formatRand } from './archiveShared';
import { formatDocumentDate } from '../../../utils/documentDates';

/** One loan's folder: its documents in six subfolders, previous versions kept, upload into each after disbursement. */
const ArchiveLoanFolder: React.FC = () => {
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const { data, isLoading, isError, refetch } = useGetArchiveLoanFolder(applicationId, { query: { enabled: !!applicationId } });
  const loan = data?.loan;
  const documents = data?.documents || [];

  if (isLoading) {
    return <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>;
  }
  if (isError || !loan) {
    return <div className="container-fluid py-5 text-center text-muted">This loan folder could not be loaded. <Link to="/documents">Back to all loan files</Link></div>;
  }
  const fileLink = `/documents/files/${data?.customerPartyId}`;

  return (
    <>
      <ArchiveHeader
        crumbs={[
          { label: 'All loan files', to: '/documents' },
          { label: data?.customerName || 'Borrower', to: fileLink },
          { label: 'Loan-specific documents', to: `${fileLink}/loans` },
          { label: loan.accountNumber || 'Loan' },
        ]}
        backTo={`${fileLink}/loans`}
        title={`Loan · ${loan.accountNumber}`}
        subtitle={
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            <span>{loan.product || 'Loan'}{loan.amount != null && <> · {formatRand(loan.amount)}</>}</span>
            <span>· Disbursed {formatDocumentDate(loan.disbursementDate)}</span>
            <LoanStatusPill status={loan.status} />
            {loan.closedDate && <span className="small">Settled {formatDocumentDate(loan.closedDate)}</span>}
            {loan.accountId && <Link to={`/loan-accounts/${loan.accountId}`} className="small text-white text-decoration-underline">Open loan account</Link>}
          </span>
        }
      />
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
      <div className="container-fluid">
        {(data?.attention || []).length > 0 && (
          <div className="alert alert-warning">
            <div className="fw-semibold mb-1">Needs attention</div>
            <AttentionList items={data?.attention || []} />
          </div>
        )}
        {LOAN_FOLDERS.map(folder => (
          <ArchiveFolderDocuments
            key={folder.key}
            folder={folder}
            documents={documents.filter(d => d.folder === folder.key)}
            uploadApplicationId={loan.loanApplicationId}
            onUploaded={() => refetch()}
          />
        ))}
      </div>
      </div>
    </>
  );
};

export default ArchiveLoanFolder;
